import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, LogIn, UserPlus, KeyRound, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PaintEdges from "../components/PaintEdges";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/profil";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Login = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const submit = (e) => {
    e.preventDefault();
    if (mode === "reset") {
      if (!validEmail) {
        toast.error("Bitte gib eine gültige E-Mail-Adresse ein.");
        return;
      }
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        toast.success("Link zum Zurücksetzen gesendet! (Demo)", {
          description: `Wir haben eine E-Mail an ${email} geschickt.`,
        });
        setMode("login");
      }, 800);
      return;
    }
    if (!validEmail || password.length < 6) {
      toast.error("Bitte gültige E-Mail und Passwort (min. 6 Zeichen) eingeben.");
      return;
    }
    if (mode === "register" && !name.trim()) {
      toast.error("Bitte gib deinen Namen ein.");
      return;
    }
    // Hinweis: E-Mail/Passwort-Login ist eine Demo (ohne Server). Für echten Login bitte Google nutzen.
    setLoading(true);
    setTimeout(() => {
      setUser({ user_id: "demo", name: name || email.split("@")[0], email });
      setLoading(false);
      toast.success(
        mode === "register" ? `Konto erstellt – willkommen, ${name || email.split("@")[0]}!` : `Willkommen zurück!`
      );
      navigate("/profil");
    }, 900);
  };

  return (
    <main className="relative overflow-hidden bg-gradient-to-b from-[#cdebfb] via-[#e3f4fd] to-white min-h-[calc(100vh-76px)]">
      {/* Erhöhter Blauanteil: Pinselstriche komplett in Blau-Nuancen */}
      <PaintEdges from="#0e6da8" via="#1b9ad6" to="#6ec6ee" opacity={0.85} />

      <div className="relative mx-auto max-w-[1440px] px-4 sm:px-8 py-14 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
        {/* Linke Seite: Text */}
        <div className="hidden lg:block">
          <p className="fade-up font-script text-2xl sm:text-3xl text-[#0e6da8]">
            Schön, dass du da bist.
          </p>
          <h1 className="fade-up-1 mt-2 font-display font-extrabold leading-[1.05] text-[clamp(2.4rem,5vw,4rem)] tracking-tight">
            <span className="text-[#1273a8]">DEIN</span><br />
            <span className="bg-gradient-to-r from-[#1b9ad6] to-[#3ec6c0] bg-clip-text text-transparent">
              TIMERSTORE<br />KONTO
            </span>
          </h1>
          <p className="fade-up-2 mt-5 max-w-md text-lg text-slate-600 leading-relaxed">
            Melde dich an, um deine Anfragen, Bestellungen und Wunschprojekte an einem Ort zu
            verwalten – individuell, kreativ, einzigartig.
          </p>
          <div className="fade-up-3 mt-8 flex items-center gap-3">
            <img src="/logo-icon.png" alt="TimerStore Sanduhr" className="h-14 w-auto" />
            <img src="/logo-text.png" alt="TimerStore" className="h-9 w-auto" />
          </div>
        </div>

        {/* Login-Karte */}
        <div className="fade-up-2 mx-auto w-full max-w-md">
          <div className="card-soft !border-sky-200 p-8 sm:p-10">
            <div className="flex justify-center mb-6">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#1273a8] to-[#1b9ad6] flex items-center justify-center shadow-lg shadow-sky-200">
                {mode === "reset" ? (
                  <KeyRound className="h-8 w-8 text-white" />
                ) : mode === "register" ? (
                  <UserPlus className="h-8 w-8 text-white" />
                ) : (
                  <LogIn className="h-8 w-8 text-white" />
                )}
              </div>
            </div>

            <h2 className="text-center font-display text-3xl font-extrabold">
              {mode === "reset" ? (
                <span className="text-[#1273a8]">Passwort zurücksetzen</span>
              ) : mode === "register" ? (
                <>
                  <span className="text-[#1273a8]">Konto</span>{" "}
                  <span className="text-[#1b9ad6]">erstellen</span>
                </>
              ) : (
                <>
                  <span className="text-[#1273a8]">Willkommen</span>{" "}
                  <span className="text-[#1b9ad6]">zurück</span>
                </>
              )}
            </h2>
            <p className="text-center text-slate-500 mt-2 text-[15px]">
              {mode === "reset"
                ? "Wir senden dir einen Link zum Zurücksetzen per E-Mail."
                : mode === "register"
                ? "Erstelle dein kostenloses TimerStore-Konto."
                : "Melde dich mit deiner E-Mail-Adresse an."}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              {mode !== "reset" && (
                <>
                  <button
                    type="button"
                    onClick={googleLogin}
                    className="w-full inline-flex items-center justify-center gap-3 rounded-full h-12 border border-slate-200 bg-white font-bold text-slate-700 text-[15px] transition-all duration-200 hover:bg-slate-50 hover:border-slate-300 hover:shadow-sm"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
                      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z" />
                    </svg>
                    Mit Google anmelden
                  </button>
                  <div className="flex items-center gap-3 py-1">
                    <span className="h-px flex-1 bg-slate-200" />
                    <span className="text-xs font-semibold text-slate-400 uppercase">oder mit E-Mail</span>
                    <span className="h-px flex-1 bg-slate-200" />
                  </div>
                </>
              )}
              {mode === "register" && (
                <div className="relative">
                  <UserPlus className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#1b9ad6]" />
                  <input
                    className="input-soft !pl-12 h-12 !border-sky-200"
                    placeholder="Dein Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              )}

              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#1b9ad6]" />
                <input
                  type="email"
                  className="input-soft !pl-12 h-12 !border-sky-200"
                  placeholder="E-Mail-Adresse"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              {mode !== "reset" && (
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#1b9ad6]" />
                  <input
                    type={showPw ? "text" : "password"}
                    className="input-soft !pl-12 !pr-12 h-12 !border-sky-200"
                    placeholder="Passwort"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label="Passwort anzeigen"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#1b9ad6] transition-colors"
                  >
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              )}

              {mode === "login" && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setMode("reset")}
                    className="text-sm font-bold text-[#1b9ad6] hover:text-[#1273a8] hover:underline transition-colors"
                  >
                    Passwort vergessen?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full h-12 text-white font-bold text-[15px] transition-all duration-300 bg-gradient-to-r from-[#1273a8] to-[#1b9ad6] shadow-[0_8px_20px_rgba(27,154,214,0.35)] hover:-translate-y-0.5 hover:shadow-[0_12px_26px_rgba(27,154,214,0.45)] disabled:opacity-60"
              >
                {mode === "reset" ? (
                  <>
                    <KeyRound className="h-5 w-5" /> {loading ? "Wird gesendet ..." : "Link senden"}
                  </>
                ) : mode === "register" ? (
                  <>
                    <UserPlus className="h-5 w-5" /> {loading ? "Wird erstellt ..." : "Konto erstellen"}
                  </>
                ) : (
                  <>
                    <LogIn className="h-5 w-5" /> {loading ? "Anmeldung ..." : "Login"}
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-[15px]">
              {mode === "login" && (
                <p className="text-slate-500">
                  Noch kein Konto?{" "}
                  <button
                    onClick={() => setMode("register")}
                    className="font-bold text-[#1b9ad6] hover:text-[#1273a8] hover:underline"
                  >
                    Jetzt registrieren
                  </button>
                </p>
              )}
              {mode === "register" && (
                <p className="text-slate-500">
                  Schon ein Konto?{" "}
                  <button
                    onClick={() => setMode("login")}
                    className="font-bold text-[#1b9ad6] hover:text-[#1273a8] hover:underline"
                  >
                    Zum Login
                  </button>
                </p>
              )}
              {mode === "reset" && (
                <button
                  onClick={() => setMode("login")}
                  className="inline-flex items-center gap-1 font-bold text-[#1b9ad6] hover:text-[#1273a8] hover:underline"
                >
                  <ArrowLeft className="h-4 w-4" /> Zurück zum Login
                </button>
              )}
            </div>
          </div>

          <p className="text-center text-sm text-slate-400 mt-6">
            Mit der Anmeldung akzeptierst du unsere{" "}
            <Link to="/" className="underline hover:text-[#1b9ad6]">AGB</Link> und{" "}
            <Link to="/" className="underline hover:text-[#1b9ad6]">Datenschutz</Link>.
          </p>
        </div>
      </div>
    </main>
  );
};

export default Login;
